import { TaskStatus } from './task-status';
import { DefaultFilters } from './default-filters';
import { WorkType, WorkTypePrimitives } from './work-type';
import { MasterDataRepository } from './master-data-repository';
import { Route } from './task';
import { Priority, PriorityPrimitives } from './priority';
import { Nullable } from 'src/core/common/domain/types/types';

export enum DeadlineFilter {
  NONE = 'none',
  BEFORE = 'before',
  ON_TIME = 'on_time',
  AFTER = 'after',
}

export enum FilterDate {
  TODAY = 'today',
  THIS_WEEK = 'this_week',
  THIS_MONTH = 'this_month',
  LAST_MONTH = 'last_month',
  RANGE = 'range',
  RANGE_SEVEN_DAYS_BEFORE_AND_AFTER = 'range_7_days_before_and_after',
  RANGE_THREE_MONTHS_BEFORE_AND_AFTER = 'range_3_months_before_and_after',
}

export enum Assignee {
  ME = 'me',
  OTHERS = 'others',
  NONE = 'none',
}

interface FiltersDatesConfiguration {
  max_days_range: number;
}

type FiltersPrimitives = {
  default: {
    work_types: Array<string>;
    statuses: Array<string>;
    date: string;
    assignee: Array<string>;
    // Optional to stay backward-compatible with filters stored before priority existed.
    priorities?: Array<number>;
    // Optional to stay backward-compatible with filters stored before route existed.
    routes?: Array<string>;
  };
  work_types: Array<WorkTypePrimitives>;
  statuses: Array<string>;
  assignees: Array<string>;
  priorities?: Array<PriorityPrimitives>;
  routes?: Array<Route>;
  dates_configuration: FiltersDatesConfiguration;
};

export class Filters {
  public dates: Array<FilterDate> = [
    FilterDate.TODAY,
    FilterDate.THIS_WEEK,
    FilterDate.THIS_MONTH,
    FilterDate.LAST_MONTH,
    FilterDate.RANGE_THREE_MONTHS_BEFORE_AND_AFTER,
    FilterDate.RANGE,
  ];

  constructor(
    public readonly defaultFilters: DefaultFilters,
    public readonly workTypes: Array<WorkType>,
    public readonly statuses: Array<TaskStatus>,
    public readonly assignees: Array<Assignee>,
    public readonly datesConfiguration: FiltersDatesConfiguration,
    public readonly priorities: Array<Priority> = [],
    public readonly routes: Array<Route> = []
  ) {
    // Sorted here rather than where the API response is mapped, so the order is the same whether
    // these filters came from the network or were rebuilt from storage.
    this.workTypes = WorkType.sortedByPosition(workTypes);
    const maxDaysRange = this.datesConfiguration.max_days_range;
    this.dates = this.prepareDatesWithinMaxRange(maxDaysRange);
  }

  private prepareDatesWithinMaxRange(maxDaysRange: number) {
    return this.dates.filter((date) => {
      let rangeDays = -1;
      switch (date) {
        case FilterDate.TODAY:
          rangeDays = 1;
          break;
        case FilterDate.THIS_WEEK:
          rangeDays = 7;
          break;
        case FilterDate.RANGE_SEVEN_DAYS_BEFORE_AND_AFTER:
          rangeDays = 15;
          break;
        case FilterDate.THIS_MONTH:
        case FilterDate.LAST_MONTH:
          rangeDays = 30;
          break;
        case FilterDate.RANGE_THREE_MONTHS_BEFORE_AND_AFTER:
          rangeDays = 210; // 7 months (current month +- 3 months)
          break;
        case FilterDate.RANGE:
          rangeDays = -1;
          break;
      }

      return rangeDays <= maxDaysRange;
    });
  }

  static fromPrimitives(filters: FiltersPrimitives) {
    const defaultDatesConfiguration = {
      max_days_range: MasterDataRepository.DEFAULT_MAX_DAYS_RANGE,
    };
    const datesConfiguration = filters.dates_configuration ?? defaultDatesConfiguration;

    return new Filters(
      DefaultFilters.fromPrimitives(filters.default),
      filters.work_types.map((workType) => WorkType.fromPrimitives(workType)),
      filters.statuses.map((status) => TaskStatus.fromPrimitives(status)),
      filters.assignees.map((assignee) => assignee as Assignee),
      datesConfiguration,
      filters.priorities?.map((priority) => Priority.fromPrimitives(priority)) ?? [],
      filters.routes ?? []
    );
  }

  toPrimitives(): FiltersPrimitives {
    return {
      default: this.defaultFilters.toPrimitives(),
      work_types: this.workTypes.map((workType) => workType.toPrimitives()),
      statuses: this.statuses.map((status) => status.toPrimitives()),
      assignees: this.assignees,
      priorities: this.priorities.map((priority) => priority.toPrimitives()),
      routes: this.routes,
      dates_configuration: this.datesConfiguration,
    };
  }

  findWorkType(id: string): Nullable<WorkType> {
    const workType = this.workTypes.find((candidate) => candidate.id === id) ?? null;
    return workType;
  }

  getMaxDaysRange(): number {
    return this.datesConfiguration.max_days_range;
  }
}
